/*
 * Copyright (c) 2020-present Amida Technology Solutions, Inc.
 * All rights reserved.
 *
 * Licensed under the Amida proprietary license.
 * See LICENSE file in the project root for license information.
 */

import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";

// ============================================================================
// Types
// ============================================================================

interface BrandingConfig {
  appName: string;
  appTagline: string;
  appDescription: string;
  stateName: string;
  helpTitle: string;
  helpIntro: string;
  logoUrl: string | null;
  logoAlt: string;
  faviconUrl: string | null;
  primaryColor: string;
  accentColor: string;
  footerText: string;
  supportEmail: string;
  supportUrl: string | null;
  documentationUrl: string | null;
}

interface BrandingState extends BrandingConfig {
  // State
  isLoaded: boolean;

  // Actions
  setBranding: (config: Partial<BrandingConfig>) => void;
  resetBranding: () => void;
  setLoaded: (isLoaded: boolean) => void;
}

// ============================================================================
// Default branding (TORQE CMS theme)
// ============================================================================

const DEFAULT_BRANDING: BrandingConfig = {
  appName: "TorQE",
  appTagline: "T-MSIS Quality Engine",
  appDescription:
    "TorQE is a comprehensive data quality management platform designed to help state Medicaid agencies monitor, analyze, and improve their T-MSIS data submissions to CMS.",
  stateName: "State",
  helpTitle: "Help & Documentation",
  helpIntro: "Learn how to use TorQE effectively and understand T-MSIS data quality requirements.",
  logoUrl: null, // Uses text logo by default
  logoAlt: "TorQE Logo",
  faviconUrl: null,
  primaryColor: "#ea580c", // Orange-600
  accentColor: "#f97316", // Orange-500
  footerText: "Centers for Medicare & Medicaid Services (CMS)",
  supportEmail: "torqe-support@cms.hhs.gov",
  supportUrl: null,
  documentationUrl: null,
};

// ============================================================================
// Store
// ============================================================================

export const useBrandingStore = create<BrandingState>()(
  persist(
    (set) => ({
      // Initial state
      ...DEFAULT_BRANDING,
      isLoaded: false,

      // Actions
      setBranding: (config) =>
        set((state) => ({
          ...state,
          ...config,
        })),

      resetBranding: () =>
        set({
          ...DEFAULT_BRANDING,
          isLoaded: true,
        }),

      setLoaded: (isLoaded) => set({ isLoaded }),
    }),
    {
      name: "torqe-branding",
      storage: createJSONStorage(() => sessionStorage),
      partialize: () => ({}), // Don't persist branding - always fetch from server
    }
  )
);

// ============================================================================
// Fine-grained Selectors
// ============================================================================

export const useAppName = () => useBrandingStore((s) => s.appName);
export const useAppTagline = () => useBrandingStore((s) => s.appTagline);
export const useAppDescription = () => useBrandingStore((s) => s.appDescription);
export const useStateName = () => useBrandingStore((s) => s.stateName);
export const useHelpTitle = () => useBrandingStore((s) => s.helpTitle);
export const useHelpIntro = () => useBrandingStore((s) => s.helpIntro);
export const useLogoUrl = () => useBrandingStore((s) => s.logoUrl);
export const useLogoAlt = () => useBrandingStore((s) => s.logoAlt);
export const usePrimaryColor = () => useBrandingStore((s) => s.primaryColor);
export const useAccentColor = () => useBrandingStore((s) => s.accentColor);
export const useFooterText = () => useBrandingStore((s) => s.footerText);
export const useSupportEmail = () => useBrandingStore((s) => s.supportEmail);
export const useSupportUrl = () => useBrandingStore((s) => s.supportUrl);
export const useDocumentationUrl = () => useBrandingStore((s) => s.documentationUrl);
export const useIsBrandingLoaded = () => useBrandingStore((s) => s.isLoaded);

// Full branding config for passing to components
export const useBrandingConfig = () =>
  useBrandingStore((s) => ({
    appName: s.appName,
    appTagline: s.appTagline,
    appDescription: s.appDescription,
    stateName: s.stateName,
    helpTitle: s.helpTitle,
    helpIntro: s.helpIntro,
    logoUrl: s.logoUrl,
    logoAlt: s.logoAlt,
    faviconUrl: s.faviconUrl,
    primaryColor: s.primaryColor,
    accentColor: s.accentColor,
    footerText: s.footerText,
    supportEmail: s.supportEmail,
    supportUrl: s.supportUrl,
    documentationUrl: s.documentationUrl,
  }));
